import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { CalendarDays, Search, RefreshCw, ArrowRight, Check, XCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDayPasses = () => {
  const { token } = useAuth();
  const [passes, setPasses] = useState([]);
  const [total, setTotal] = useState(0);
  const [conversions, setConversions] = useState(null);
  const [siteFilter, setSiteFilter] = useState('');
  const [sites, setSites] = useState([]);
  const [page, setPage] = useState(1);
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    fetch(`${API_URL}/api/sites`, { headers }).then(r => r.json()).then(d => setSites(d.items || [])).catch(() => {});
  }, [token]);

  const fetchPasses = useCallback(async () => {
    const p = new URLSearchParams({ page: String(page), page_size: '50' });
    if (siteFilter) p.append('site_id', siteFilter);
    const res = await fetch(`${API_URL}/api/day-pass/admin/list?${p}`, { headers });
    if (res.ok) {
      const d = await res.json();
      setPasses(d.items || []);
      setTotal(d.total || 0);
    }
  }, [page, siteFilter]);

  const fetchConversions = useCallback(async () => {
    const res = await fetch(`${API_URL}/api/day-pass/admin/conversions`, { headers });
    if (res.ok) setConversions(await res.json());
  }, []);

  useEffect(() => { fetchPasses(); fetchConversions(); }, [fetchPasses, fetchConversions]);

  const statusColors = {
    active: 'bg-emerald-500/20 text-emerald-400',
    used: 'bg-blue-500/20 text-blue-400',
    expired: 'bg-gray-500/20 text-gray-400',
    cancelled: 'bg-red-500/20 text-red-400',
  };

  return (
    <div className="p-4 lg:p-8" data-testid="admin-day-passes">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-uf-text flex items-center gap-2"><CalendarDays className="w-6 h-6 text-uf-red" />Day Passes</h1>
          <p className="text-uf-text-muted text-sm mt-1">{total} total day passes</p>
        </div>
        <button onClick={() => { fetchPasses(); fetchConversions(); }} className="flex items-center gap-2 bg-uf-surface border border-uf-border rounded-lg px-4 py-2 text-uf-text text-sm hover:bg-uf-surface-hi" data-testid="refresh-passes">
          <RefreshCw className="w-4 h-4" />Refresh
        </button>
      </div>

      {/* Conversion Stats */}
      {conversions && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className="bg-blue-500/10 rounded-xl p-4">
            <p className="text-uf-text-muted text-xs uppercase tracking-wider">Total Sold</p>
            <p className="text-2xl font-bold text-blue-400 mt-1">{conversions.total_passes || 0}</p>
          </div>
          <div className="bg-emerald-500/10 rounded-xl p-4">
            <p className="text-uf-text-muted text-xs uppercase tracking-wider">Converted to Member</p>
            <p className="text-2xl font-bold text-emerald-400 mt-1">{conversions.converted_to_member || conversions.converted || 0}</p>
          </div>
          <div className="bg-violet-500/10 rounded-xl p-4">
            <p className="text-uf-text-muted text-xs uppercase tracking-wider">Conversion Rate</p>
            <p className="text-2xl font-bold text-violet-400 mt-1">{conversions.conversion_rate || 0}%</p>
          </div>
          <div className="bg-amber-500/10 rounded-xl p-4">
            <p className="text-uf-text-muted text-xs uppercase tracking-wider">Revenue</p>
            <p className="text-2xl font-bold text-amber-400 mt-1">£{(conversions.total_revenue || 0).toFixed(2)}</p>
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="flex gap-3 mb-4">
        <select value={siteFilter} onChange={e => setSiteFilter(e.target.value)}
          style={{ width: 'auto', minWidth: '180px' }}
          className="bg-uf-surface border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm" data-testid="pass-site-filter">
          <option value="">All Sites</option>
          {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="bg-uf-surface rounded-xl border border-uf-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-uf-border text-left">
              <th className="p-3 text-uf-text-muted font-medium">Guest</th>
              <th className="p-3 text-uf-text-muted font-medium">Site</th>
              <th className="p-3 text-uf-text-muted font-medium">Status</th>
              <th className="p-3 text-uf-text-muted font-medium">Visit Date</th>
              <th className="p-3 text-uf-text-muted font-medium">Amount</th>
              <th className="p-3 text-uf-text-muted font-medium">Created</th>
            </tr></thead>
            <tbody>
              {passes.map(p => (
                <tr key={p.id} className="border-b border-uf-border/50 hover:bg-uf-surface-hi" data-testid={`pass-row-${p.id}`}>
                  <td className="p-3">
                    <p className="text-uf-text font-medium">{p.guest_name || 'Unknown'}</p>
                    <p className="text-uf-text-muted text-xs">{p.guest_email || ''}</p>
                  </td>
                  <td className="p-3 text-uf-text-muted">{p.site_name || '—'}</td>
                  <td className="p-3"><span className={`px-2 py-0.5 rounded text-xs capitalize ${statusColors[p.status] || 'bg-gray-500/20 text-gray-400'}`}>{p.status}</span></td>
                  <td className="p-3 text-uf-text-muted">{p.visit_date || '—'}</td>
                  <td className="p-3 text-uf-text font-medium">£{(p.amount_paid || 0).toFixed(2)}</td>
                  <td className="p-3 text-uf-text-muted">{p.created_at?.split('T')[0] || '—'}</td>
                </tr>
              ))}
              {!passes.length && <tr><td colSpan={6} className="p-8 text-center text-uf-text-muted">No day passes found</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {total > 50 && (
        <div className="flex justify-center gap-2 mt-4">
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1.5 bg-uf-surface border border-uf-border rounded text-uf-text text-sm disabled:opacity-50">Prev</button>
          <span className="px-3 py-1.5 text-uf-text-muted text-sm">Page {page}</span>
          <button onClick={() => setPage(p => p + 1)} className="px-3 py-1.5 bg-uf-surface border border-uf-border rounded text-uf-text text-sm">Next</button>
        </div>
      )}
    </div>
  );
};

export default AdminDayPasses;
